import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  CheckCircle2,
  ClipboardPaste,
  CloudOff,
  Eye,
  Loader2,
  Lock,
  OctagonAlert,
  Plus,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Main } from "@/components/Main";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import {
  analyzeGameBankSheet,
  createGameBankSheet,
  exportGameBankSheet,
  ExportFormat,
  failedMessage,
  failedSheet,
  failedStatus,
  finishGameBankSheet,
  getGameBankLotteries,
  getGameBankSheet,
  GameBankLottery,
  GameBankSheet,
  GameBankSheetCheckStatus,
  GameBankSheetStatus,
  GameBankSheetSummary,
  unarchiveGameBankSheet,
  updateGameBankSheet,
} from "@/Api/gameBank";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { cn } from "@/lib/utils";
import { CheckStatusBadge, formatDateTime, LotteryBadge, pad2, SheetStatusBadge } from "./shared";
import { ExportMenu, fileSlug } from "./ExportMenu";
import { SheetGrid } from "./sheet/SheetGrid";
import {
  buildResults,
  emptyCells,
  freshStatus,
  hasLocalProblem,
  insertRows,
  isFilled,
  isIncomplete,
  makeRow,
  MAX_ROWS,
  parseClipboard,
  pasteMatrix,
  RowResult,
  SheetRow,
  toSheetRows,
} from "./sheet/model";

const INITIAL_ROWS = 5;
/** Linhas que o botão "Adicionar linhas" cria */
const ADD_ROWS = 5;
/** Espera depois da última alteração para gravar a planilha */
const AUTOSAVE_DELAY = 1500;
/** Passos guardados para desfazer */
const HISTORY_LIMIT = 100;

type AutosaveState = "idle" | "saving" | "saved" | "error" | "conflict";

/** Planilha conhecida pela tela: os campos do autosave mais os completos quando vierem */
type SheetState = GameBankSheetSummary & Partial<Pick<GameBankSheet, "saved_by" | "archived_by">>;

const blankRows = (columns: number) => Array.from({ length: INITIAL_ROWS }, () => makeRow(emptyCells(columns)));

const toApiRows = (rows: SheetRow[]) => rows.map((row) => row.cells);

/** Indicador do autosave no cabeçalho */
const AutosaveIndicator = ({ state, savedAt, onRetry }: { state: AutosaveState; savedAt: Date | null; onRetry: () => void }) => {
  if (state === "saving") {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Salvando…
      </span>
    );
  }

  if (state === "error") {
    return (
      <button type="button" onClick={onRetry} className="flex items-center gap-1.5 text-xs font-medium text-red-600">
        <CloudOff className="h-3.5 w-3.5" />
        Erro ao salvar · tentar de novo
      </button>
    );
  }

  if (state === "saved" && savedAt) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
        Salvo às {savedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
      </span>
    );
  }

  return null;
};

/**
 * Banco de Bolões > Banco de Jogos > Planilha (LOT-62).
 * A grade (SheetGrid) tem os recursos do Excel: seleção de área, copiar e colar, desfazer, mover linhas e
 * colunas, localizar e filtrar. A página guarda as linhas, o histórico do desfazer e o autosave;
 * "Conferir jogos" compara cada linha com o banco inteiro da loteria e "Finalizar planilha" joga os jogos no
 * banco. Finalizada ou arquivada, fica somente leitura.
 */
export const GameBankRegisterPage = () => {
  const navigate = useNavigate();
  const { lotteryId, sheetKey } = useParams();
  const [searchParams] = useSearchParams();
  const { hasPermission, isAdmin } = usePermissions();
  const canManage = isAdmin() || hasPermission(PERMISSIONS.MANAGE_GAME_BANK);

  const initialColumns = sheetKey === "nova" ? Number(searchParams.get("dezenas")) || 0 : 0;

  const [lottery, setLottery] = useState<GameBankLottery | null>(null);
  const [lotteryLoaded, setLotteryLoaded] = useState(false);
  const [sheet, setSheet] = useState<SheetState | null>(null);
  const [sheetLoading, setSheetLoading] = useState(sheetKey !== "nova");
  const [notFound, setNotFound] = useState(false);
  const [columns, setColumns] = useState(initialColumns);
  const [rows, setRows] = useState<SheetRow[]>(() => blankRows(initialColumns));
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const [status, setStatus] = useState<GameBankSheetStatus>("in_progress");
  const [checkStatus, setCheckStatus] = useState<GameBankSheetCheckStatus>("pending");
  const [autosave, setAutosave] = useState<AutosaveState>("idle");
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [history, setHistory] = useState<{ past: SheetRow[][]; future: SheetRow[][] }>({ past: [], future: [] });

  const uuidRef = useRef<string | null>(null);
  const versionRef = useRef(0);
  const rowsRef = useRef(rows);
  const dirtyRef = useRef(false);
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const conflictRef = useRef(false);

  const rules = lottery?.rules;
  const readOnly = !canManage || status !== "in_progress";
  // finalizando: as células não mudam até a resposta (se der certo, a planilha fica somente leitura)
  const locked = readOnly || saving;
  const readOnlyRef = useRef(locked);

  useEffect(() => {
    readOnlyRef.current = locked;
  }, [locked]);

  useEffect(() => {
    conflictRef.current = conflict !== null;
  }, [conflict]);

  useEffect(() => {
    getGameBankLotteries()
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data?.response)) {
          setLottery(data.response.find((item: GameBankLottery) => item.id === Number(lotteryId)) ?? null);
        }
      })
      .finally(() => setLotteryLoaded(true));
  }, [lotteryId]);

  /** Troca todas as linhas da tela (carregar, recarregar, finalizar): o histórico recomeça */
  const replaceRows = useCallback((next: SheetRow[]) => {
    rowsRef.current = next;
    setRows(next);
    setHistory({ past: [], future: [] });
    dirtyRef.current = false;
  }, []);

  /**
   * Aplica a planilha devolvida pela API. `analyzedRows`: as linhas que foram conferidas (para ligar o
   * resultado aos ids); sem elas, as linhas da tela são trocadas pelas gravadas.
   */
  const applySheet = useCallback(
    (data: GameBankSheet, analyzedRows?: SheetRow[]) => {
      uuidRef.current = data.uuid;
      versionRef.current = data.version;
      setSheet(data);
      setStatus(data.status);
      setCheckStatus(data.check_status);
      setColumns(data.dezenas);

      let base = analyzedRows;
      if (!base) {
        base = toSheetRows(data.rows, data.dezenas);
        replaceRows(base);
      }

      setResults(data.check_status === "analyzed" && data.analysis ? buildResults(data.analysis.rows, base) : {});
    },
    [replaceRows]
  );

  const loadSheet = useCallback(
    async (uuid: string) => {
      setSheetLoading(true);
      try {
        const { ok, data } = await getGameBankSheet(uuid);
        if (ok && data?.response) {
          applySheet(data.response);
          setConflict(null);
          setAutosave("idle");
        } else {
          setNotFound(true);
        }
      } finally {
        setSheetLoading(false);
      }
    },
    [applySheet]
  );

  useEffect(() => {
    // depois do primeiro autosave a URL troca de "nova" para o uuid: a tela já está com a planilha
    if (!sheetKey || sheetKey === "nova" || sheetKey === uuidRef.current) return;
    loadSheet(sheetKey);
  }, [sheetKey, loadSheet]);

  /** Planilha nova: Super Sete tem quantidade fixa de números por jogo */
  useEffect(() => {
    if (sheetKey !== "nova" || !lottery?.rules.length || columns === lottery.rules.length) return;
    const length = lottery.rules.length;
    setColumns(length);
    replaceRows(blankRows(length));
  }, [sheetKey, lottery, columns, replaceRows]);

  const handleConflict = useCallback(
    (result: unknown) => {
      setAutosave("conflict");
      setConflict(failedMessage(result) ?? "Esta planilha foi alterada por outra pessoa. Recarregue para continuar.");
      const current = failedSheet(result);
      if (current && current.status !== "in_progress") applySheet(current);
    },
    [applySheet]
  );

  /**
   * Grava o que estiver pendente. Uma gravação por vez: quem chama durante uma gravação espera ela terminar
   * e grava de novo se ainda houver alteração. Cria a planilha no primeiro jogo digitado.
   */
  const persist = useCallback(async (): Promise<boolean> => {
    while (inFlightRef.current) await inFlightRef.current;
    if (!dirtyRef.current || readOnlyRef.current || conflictRef.current) return !dirtyRef.current;

    const snapshot = toApiRows(rowsRef.current);
    dirtyRef.current = false;

    const run = (async () => {
      const creating = !uuidRef.current;

      if (creating && !snapshot.some(isFilled)) return true;

      setAutosave("saving");
      const result = creating
        ? await createGameBankSheet({ lottery_id: Number(lotteryId), dezenas: columns, rows: snapshot })
        : await updateGameBankSheet(uuidRef.current as string, { rows: snapshot, version: versionRef.current });

      if (result.ok && result.data?.response) {
        const saved: GameBankSheetSummary = result.data.response;
        uuidRef.current = saved.uuid;
        versionRef.current = saved.version;
        setSheet((previous) => ({ ...previous, ...saved }));
        setStatus(saved.status);
        setCheckStatus((current) => (dirtyRef.current && current === "pending" ? current : saved.check_status));
        setAutosave("saved");
        setSavedAt(new Date());
        if (creating) navigate(`/banco-de-boloes/${lotteryId}/planilhas/${saved.uuid}`, { replace: true });
        return true;
      }

      dirtyRef.current = true;
      if (failedStatus(result) === 409) handleConflict(result);
      else setAutosave("error");
      return false;
    })();

    inFlightRef.current = run;
    try {
      return await run;
    } finally {
      if (inFlightRef.current === run) inFlightRef.current = null;
    }
  }, [lotteryId, columns, navigate, handleConflict]);

  const persistRef = useRef(persist);
  useEffect(() => {
    persistRef.current = persist;
  }, [persist]);

  /** Toda alteração na planilha: a conferência volta para Pendente e o autosave é agendado */
  const markEdited = useCallback(() => {
    dirtyRef.current = true;
    setCheckStatus((current) => (current === "analyzed" ? "pending" : current));
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      void persistRef.current();
    }, AUTOSAVE_DELAY);
  }, []);

  /** Grava uma alteração das células no histórico (desfazer) e agenda o autosave */
  const commitRows = useCallback(
    (next: SheetRow[]) => {
      const current = rowsRef.current;
      if (next === current || readOnlyRef.current) return;
      setHistory((previous) => ({ past: [...previous.past, current].slice(-HISTORY_LIMIT), future: [] }));
      rowsRef.current = next;
      setRows(next);
      markEdited();
    },
    [markEdited]
  );

  const undo = useCallback(() => {
    if (readOnlyRef.current || !history.past.length) return;
    const previous = history.past[history.past.length - 1];
    setHistory({ past: history.past.slice(0, -1), future: [rowsRef.current, ...history.future] });
    rowsRef.current = previous;
    setRows(previous);
    markEdited();
  }, [history, markEdited]);

  const redo = useCallback(() => {
    if (readOnlyRef.current || !history.future.length) return;
    const [next, ...rest] = history.future;
    setHistory({ past: [...history.past, rowsRef.current].slice(-HISTORY_LIMIT), future: rest });
    rowsRef.current = next;
    setRows(next);
    markEdited();
  }, [history, markEdited]);

  // Sai da tela com alteração pendente: grava na hora e avisa se a aba for fechada antes
  useEffect(() => {
    const dirty = dirtyRef;
    const inFlight = inFlightRef;
    const timer = timerRef;
    const flush = persistRef;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty.current || inFlight.current) event.preventDefault();
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      window.clearTimeout(timer.current);
      if (dirty.current) void flush.current();
    };
  }, []);

  const addRows = () => {
    const result = insertRows(rows, rows.length, ADD_ROWS, columns);
    if (!result.inserted) {
      toast.warning(`Máximo de ${MAX_ROWS} jogos por planilha.`);
      return;
    }
    commitRows(result.rows);
  };

  /** "Colar do Excel": entra a partir da primeira linha vazia */
  const importPasted = () => {
    if (!rules) return;
    const matrix = parseClipboard(pasteText, rules);
    if (!matrix.length) return;
    const firstEmpty = rows.findIndex((row) => !isFilled(row.cells));
    const start = firstEmpty === -1 ? rows.length : firstEmpty;
    const result = pasteMatrix(rows, rows.map((_, index) => index), { r1: start, r2: start, c1: 0, c2: 0 }, matrix, columns, true);
    commitRows(result.rows);
    if (result.trimmed) toast.warning(`Havia mais de ${columns} números em alguma linha; o excedente foi ignorado.`);
    if (result.capped) toast.warning(`Máximo de ${MAX_ROWS} jogos por planilha; o excedente foi ignorado.`);
    setPasteText("");
    setPasteOpen(false);
  };

  const filledRows = useMemo(() => rows.filter((row) => isFilled(row.cells)), [rows]);

  const localProblems = useMemo(
    () => (rules ? filledRows.some((row) => hasLocalProblem(row.cells, rules)) : false),
    [filledRows, rules]
  );

  const incomplete = useMemo(() => filledRows.some((row) => isIncomplete(row.cells)), [filledRows]);

  // Contadores só com o que está conferido e não mudou
  const counters = useMemo(() => {
    const total = { ok: 0, attention: 0, repeated: 0, invalid: 0 };

    filledRows.forEach((row) => {
      const current = freshStatus(row, results);
      if (current === "ok") total.ok++;
      else if (current === "attention" || current === "high") total.attention++;
      else if (current === "repeated") total.repeated++;
      else if (current === "invalid") total.invalid++;
    });

    return total;
  }, [filledRows, results]);

  const busy = checking || saving;
  const canCheck = !readOnly && !conflict && filledRows.length > 0 && !incomplete && !localProblems && !busy;
  const canFinish = !readOnly && !conflict && checkStatus === "analyzed" && !!sheet?.can_finish && !busy;

  const handleCheck = async () => {
    if (incomplete) {
      toast.error(`Preencha as ${columns} dezenas de cada jogo.`);
      return;
    }

    setChecking(true);
    try {
      window.clearTimeout(timerRef.current);
      while (inFlightRef.current) await inFlightRef.current;

      if (!uuidRef.current) {
        dirtyRef.current = true;
        const created = await persist();
        if (!created || !uuidRef.current) return;
      }

      const sentRows = rowsRef.current;
      dirtyRef.current = false;

      // a conferência também grava as células: o autosave espera ela terminar para usar a versão nova
      const request = analyzeGameBankSheet(uuidRef.current, { rows: toApiRows(sentRows), version: versionRef.current });
      const tracked = request.then(
        () => true,
        () => false
      );
      inFlightRef.current = tracked;
      let result: Awaited<typeof request>;
      try {
        result = await request;
      } finally {
        if (inFlightRef.current === tracked) inFlightRef.current = null;
      }

      if (result.ok && result.data?.response) {
        const analyzed: GameBankSheet = result.data.response;
        applySheet(analyzed, sentRows);
        setAutosave("saved");
        setSavedAt(new Date());

        // mexeu na planilha durante a conferência: a conferência vale para as linhas enviadas
        if (rowsRef.current !== sentRows) markEdited();

        const { repeated, invalid, attention } = analyzed.summary;
        if (repeated > 0) toast.error("Há jogos repetidos. Remova ou altere para finalizar.");
        else if (invalid > 0) toast.error("Há linhas inválidas.");
        else if (attention > 0) toast.warning("Conferido: há jogos com alta coincidência (atenção).");
        else toast.success("Todos os jogos conferidos.");
        return;
      }

      dirtyRef.current = true;
      if (failedStatus(result) === 409) handleConflict(result);
    } finally {
      setChecking(false);
    }
  };

  const handleFinish = async () => {
    if (!uuidRef.current || !canFinish) return;

    setSaving(true);
    try {
      const result = await finishGameBankSheet(uuidRef.current);

      if (result.ok && result.data?.response) {
        const finished: GameBankSheet = result.data.response;
        applySheet(finished);
        toast.success(
          `Planilha finalizada: ${finished.rows_count} ${finished.rows_count === 1 ? "jogo cadastrado" : "jogos cadastrados"} no banco.`
        );
        return;
      }

      // conferência desatualizada: voltou para Pendente (a mensagem já aparece no aviso da API)
      const current = failedSheet(result);
      if (failedStatus(result) === 409 && current) applySheet(current, rowsRef.current);
    } finally {
      setSaving(false);
    }
  };

  /** Exporta o que está gravado: antes, grava o que o autosave ainda não mandou */
  const handleExport = async (format: ExportFormat) => {
    window.clearTimeout(timerRef.current);
    if (dirtyRef.current) await persist();
    while (inFlightRef.current) await inFlightRef.current;
    return exportGameBankSheet(uuidRef.current as string, format);
  };

  const handleUnarchive = async () => {
    if (!uuidRef.current) return;

    setUnarchiving(true);
    try {
      const result = await unarchiveGameBankSheet(uuidRef.current);
      if (result.ok && result.data?.response) {
        const restored: GameBankSheet = result.data.response;
        applySheet(restored);
        toast.success(
          restored.status === "finished"
            ? "Planilha desarquivada."
            : "Planilha desarquivada. Confira os jogos de novo antes de finalizar."
        );
      }
    } finally {
      setUnarchiving(false);
    }
  };

  const backTo = `/banco-de-boloes?loteria=${lotteryId}`;

  if (!lotteryLoaded || sheetLoading) {
    return (
      <Main>
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </Main>
    );
  }

  if (!lottery || !rules || !columns || notFound) {
    return (
      <Main>
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {notFound ? "Planilha não encontrada." : "Loteria não encontrada no Banco de Jogos."}{" "}
            <Link to={backTo} className="font-medium text-blue-600">
              Voltar
            </Link>
          </CardContent>
        </Card>
      </Main>
    );
  }

  const legendLimit = columns - 3;
  const invalidatedMessage =
    status === "in_progress" && checkStatus === "pending" && sheet?.invalidated_at
      ? sheet.invalidated_by_sheet_number
        ? `a Planilha ${sheet.invalidated_by_sheet_number} foi finalizada em ${formatDateTime(sheet.invalidated_at)}. Confira os jogos de novo.`
        : "o banco mudou depois da conferência. Confira os jogos de novo."
      : null;

  return (
    <Main>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <Link
            to={backTo}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para o Banco de Jogos
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {sheet ? `Planilha ${sheet.number}` : "Nova planilha"}
            </h1>
            <LotteryBadge name={lottery.name} color={lottery.color} />
            <SheetStatusBadge status={status} />
            {status === "in_progress" && <CheckStatusBadge status={checkStatus} />}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span>
              {lottery.name} · {columns} dezenas por jogo
              {sheet?.created_by && ` · criada por ${sheet.created_by.name}`}
            </span>
            {sheet && (
              <ExportMenu
                size="sm"
                fileName={fileSlug(`planilha ${sheet.number} ${lottery.name} ${pad2(columns)} dezenas`)}
                request={handleExport}
                disabled={!sheet.rows_count && !filledRows.length}
              />
            )}
            {!readOnly && (
              <AutosaveIndicator
                state={autosave}
                savedAt={savedAt}
                onRetry={() => {
                  dirtyRef.current = true;
                  void persist();
                }}
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:w-[34rem]">
          <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900/40 dark:bg-green-950/30">
            <CheckCircle2 className="h-7 w-7 shrink-0 text-green-600" />
            <div>
              <p className="text-2xl font-bold leading-none">{counters.ok}</p>
              <p className="text-xs text-muted-foreground">Jogos conferidos</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900/40 dark:bg-yellow-950/30">
            <AlertTriangle className="h-7 w-7 shrink-0 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold leading-none">{counters.attention}</p>
              <p className="text-xs text-muted-foreground">Atenção</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/40 dark:bg-red-950/30">
            <OctagonAlert className="h-7 w-7 shrink-0 text-red-600" />
            <div>
              <p className="text-2xl font-bold leading-none">{counters.repeated}</p>
              <p className="text-xs text-muted-foreground">Jogos repetidos</p>
            </div>
          </div>
        </div>
      </div>

      {conflict && (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200 sm:flex-row sm:items-center sm:justify-between">
          <span>{conflict} As alterações desta tela não foram gravadas.</span>
          <Button
            type="button"
            className={appDialogSecondaryButton}
            onClick={() => uuidRef.current && loadSheet(uuidRef.current)}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Recarregar planilha
          </Button>
        </div>
      )}

      {invalidatedMessage && !conflict && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          A conferência voltou para Pendente: {invalidatedMessage}
        </div>
      )}

      {status === "finished" && sheet && (
        <div className="flex flex-col gap-3 rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-900 dark:border-green-800 dark:bg-green-950/30 dark:text-green-200 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <Lock className="h-4 w-4 shrink-0" />
            Finalizada em {formatDateTime(sheet.saved_at)}
            {sheet.saved_by && ` por ${sheet.saved_by.name}`}: {sheet.rows_count}{" "}
            {sheet.rows_count === 1 ? "jogo" : "jogos"} no banco. A planilha é somente leitura.
          </span>
          <Button asChild className={appDialogSecondaryButton}>
            <Link to={`/banco-de-boloes/${lotteryId}/jogos/${columns}`}>
              <Eye className="mr-2 h-4 w-4" />
              Ver jogos no banco
            </Link>
          </Button>
        </div>
      )}

      {status === "archived" && sheet && (
        <div className="flex flex-col gap-3 rounded-lg border border-slate-300 bg-slate-50 p-4 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <Archive className="h-4 w-4 shrink-0" />
            Arquivada em {formatDateTime(sheet.archived_at)}
            {sheet.archived_by && ` por ${sheet.archived_by.name}`}.{" "}
            {sheet.saved_at
              ? `Os jogos estão no banco desde ${formatDateTime(sheet.saved_at)}.`
              : "Os jogos dela não foram para o banco."}
          </span>
          {canManage && (
            <Button type="button" className={appDialogSecondaryButton} onClick={handleUnarchive} disabled={unarchiving}>
              {unarchiving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArchiveRestore className="mr-2 h-4 w-4" />
              )}
              Desarquivar
            </Button>
          )}
        </div>
      )}

      <Card>
        <CardContent className="space-y-4 p-4 sm:p-6">
          {!readOnly && (
            <p className="text-sm text-muted-foreground">
              Digite uma dezena por célula ou cole do Excel (Ctrl+V). Arraste para selecionar uma área; o botão direito
              abre as ações. A planilha é salva automaticamente.
            </p>
          )}

          <SheetGrid
            rows={rows}
            columns={columns}
            rules={rules}
            readOnly={locked}
            results={results}
            canUndo={!locked && history.past.length > 0}
            canRedo={!locked && history.future.length > 0}
            undoSteps={history.past.length}
            onCommit={commitRows}
            onUndo={undo}
            onRedo={redo}
            autoFocus={sheetKey === "nova"}
          />

          {!readOnly && (
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                <Button type="button" className={appDialogSecondaryButton} onClick={addRows}>
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar {ADD_ROWS} linhas
                </Button>
                <Button type="button" className={appDialogSecondaryButton} onClick={() => setPasteOpen(true)}>
                  <ClipboardPaste className="mr-2 h-4 w-4" />
                  Colar do Excel
                </Button>
              </div>

              <div className="flex flex-col items-stretch gap-1 sm:items-end">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    className={cn(appDialogSecondaryButton, "order-3 sm:order-1")}
                    onClick={() => navigate(backTo)}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="button"
                    className={cn(appDialogPrimaryButton, "order-1 sm:order-2")}
                    onClick={handleCheck}
                    disabled={!canCheck}
                  >
                    {checking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Conferir jogos
                  </Button>
                  <Button
                    type="button"
                    className="order-2 bg-orange-500 text-white hover:bg-orange-600 sm:order-3"
                    onClick={handleFinish}
                    disabled={!canFinish}
                    title="Os jogos entram no banco e a planilha fica somente leitura"
                  >
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Finalizar planilha
                  </Button>
                </div>
                <p className="text-xs text-red-600 sm:text-right">
                  {checkStatus === "analyzed" && sheet && !sheet.can_finish
                    ? "Remova ou altere os jogos em vermelho e confira de novo para finalizar."
                    : filledRows.length > 0 && checkStatus === "pending"
                      ? "Confira os jogos antes de finalizar a planilha."
                      : localProblems
                        ? "Corrija as células em vermelho."
                        : " "}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
            {legendLimit >= 1 && (
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                Até {legendLimit}: verde
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
              {columns - 2}: atenção
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-orange-500" />
              {columns - 1}: alta coincidência
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
              {columns}: repetido, cadastro bloqueado
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Colar do Excel */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <AppDialogContent
          icon={ClipboardPaste}
          title="Colar do Excel"
          description="Uma linha por jogo, com as dezenas separadas por tab, espaço, vírgula ou ponto e vírgula."
          className="sm:max-w-2xl"
        >
          <AppDialogBody>
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={10}
              placeholder={"01 02 03 04 05 ...\n01 02 03 04 06 ..."}
              className="font-mono text-sm"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Os jogos entram a partir da primeira linha vazia. Máximo de {MAX_ROWS} jogos por planilha. Também dá para
              colar direto na planilha com Ctrl+V.
            </p>
          </AppDialogBody>
          <AppDialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button className={appDialogSecondaryButton} onClick={() => setPasteOpen(false)}>
              Cancelar
            </Button>
            <Button className={appDialogPrimaryButton} onClick={importPasted} disabled={!pasteText.trim()}>
              Importar jogos
            </Button>
          </AppDialogFooter>
        </AppDialogContent>
      </Dialog>
    </Main>
  );
};
